import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  CheckCircle,
  Target,
  Award,
  TrendingUp,
  Building2,
  Network,
  Cable,
  Shield,
  Monitor,
  Package,
  Video
} from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

import { Button } from '../../components/ui/button';
import { getServiceBySlug, getRelatedServices } from '../../data/services';

const iconBySlug = {
  'broadcom-migration': Network,
  'office-recabling': Cable,
  'switch-firewall': Shield,
  'audio-video-solutions': Video,
  'it-equipment-warehousing': Package,
  'conferencing-solutions': Monitor
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = useMemo(() => getServiceBySlug(slug), [slug]);
  const related = useMemo(() => getRelatedServices(slug), [slug]);
  const [openFaq, setOpenFaq] = useState(null);

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  if (!service) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20 min-h-screen">
        <div className="text-6xl mb-4">😕</div>
        <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
        <p className="text-xl text-gray-600 mb-8">
          Sorry, the service you are looking for does not exist.
        </p>
        <Link to="/services">
          <Button className="bg-gradient-to-r from-blue-600 to-cyan-500">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Services
          </Button>
        </Link>
      </div>
    );
  }

  const Icon = iconBySlug[service.slug] || Building2;
  const allImages = service.images || [];
  const heroImages = allImages.slice(0, 3);
  const albumImages = allImages;
  const combinedImages = allImages;

  const openGalleryAt = (index) => {
    setGalleryStartIndex(index);
    setGalleryOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{service.title} - Tasks IT Services</title>
        <meta name="description" content={service.summary} />
      </Helmet>

      {/* HERO (text fixed, images slide behind) */}
      <section className="relative overflow-hidden">
        <div className="relative h-[70vh]">
          {heroImages.length > 0 ? (
            <>
              <Swiper
                modules={[Autoplay, Pagination]}
                autoplay={{ delay: 4500, disableOnInteraction: false }}
                pagination={{ clickable: true }}
                className="h-full"
              >
                {heroImages.map((src, i) => (
                  <SwiperSlide key={i}>
                    <div
                      className="relative h-full cursor-zoom-in z-0" // added z-0 for stacking
                      onClick={() => openGalleryAt(i)}
                    >
                      <img
                        src={src}
                        alt={`${service.title} hero ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/85 via-indigo-900/70 to-purple-900/60" />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Static overlay content (fixed, now above slides) */}
              <div className="absolute inset-0 flex items-center z-10"> {/* added z-10 */}
                <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8">
                  <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl text-white"
                  >
                    <Link
                      to="/services"
                      className="inline-flex items-center text-blue-200 hover:text-white mb-6 transition-colors"
                    >
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Back to Services
                    </Link>
                    <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-white/20">
                      <Icon className="h-5 w-5" />
                      <span className="text-sm">{service.complexity} Project</span>
                    </div>
                    <h1 className="text-5xl md:text-6xl font-bold leading-tight mb-4">
                      {service.title}
                    </h1>
                    <p className="text-xl text-blue-100">{service.summary}</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                      <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full">
                        <Clock className="h-4 w-4" /> {service.timeline}
                      </div>
                      <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full">
                        <Award className="h-4 w-4" /> {service.stats?.satisfaction}% satisfaction
                      </div>
                      <div className="flex items-center gap-2 bg-white/10 px-3 py-2 rounded-full">
                        <TrendingUp className="h-4 w-4" /> {service.stats?.projects}+ projects
                      </div>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-4">
                      <Link to="/contact">
                        <Button className="bg-white text-blue-600 hover:bg-blue-50 shadow-xl">
                          Request Quote
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <a href="#details">
                        <Button className="bg-white/10 border border-white/20 hover:bg-white/20">
                          Learn More
                        </Button>
                      </a>
                    </div>
                  </motion.div>
                </div>
              </div>
            </>
          ) : (
            <div className="relative h-full bg-gray-100 flex items-center justify-center">
              <div className="text-center">
                <div className="text-3xl text-gray-500">No images</div>
                <div className="text-sm text-gray-400">Contact admin to add service images</div>
              </div>
            </div>
          )}
        </div>
      </section>


      <section id="details" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className={`w-10 h-10 rounded-lg bg-gradient-to-r ${service.accent} flex items-center justify-center`}
                >
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-3xl font-bold">Overview</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                {service.description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-3xl font-bold">Key Features</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {service.features.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm hover:shadow-md"
                  >
                    <CheckCircle className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{f}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <Target className="h-5 w-5 text-white" />
                </div>
                <h2 className="text-3xl font-bold">Deliverables</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {service.deliverables.map((d, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                  >
                    {d}
                  </span>
                ))}
              </div>
            </motion.div>

            {albumImages.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold">Gallery</h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {albumImages.map((src, idx) => (
                    <button
                      key={idx}
                      onClick={() => openGalleryAt(idx)}
                      className="group overflow-hidden rounded-lg"
                    >
                      <div className="relative h-36 bg-gray-100">
                        <img
                          src={src}
                          alt={`gallery ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-4 text-sm text-gray-500">
                  Click any image to open full-screen viewer.
                </div>
              </motion.div>
            )}

            {service.faqs?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
                    <Award className="h-5 w-5 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold">FAQs</h2>
                </div>
                <div className="space-y-3">
                  {service.faqs.map((faq, i) => (
                    <div
                      key={i}
                      className="border border-gray-200 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() =>
                          setOpenFaq(openFaq === i ? null : i)
                        }
                        className="w-full flex items-center justify-between text-left px-4 py-3 bg-gray-50 hover:bg-gray-100"
                      >
                        <span className="font-semibold text-gray-800">
                          {faq.q}
                        </span>
                        <span className="text-sm text-gray-500">
                          {openFaq === i ? '−' : '+'}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {openFaq === i && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="px-4 py-3 bg-white text-gray-700"
                          >
                            {faq.a}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-2xl shadow-lg sticky top-24">
              <h3 className="text-xl font-bold mb-4">Project Snapshot</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl text-center">
                  <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <div className="text-xs text-gray-500">Timeline</div>
                  <div className="font-semibold">{service.timeline}</div>
                </div>
                <div className="bg-white p-4 rounded-xl text-center">
                  <Target className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <div className="text-xs text-gray-500">Complexity</div>
                  <div className="font-semibold">{service.complexity}</div>
                </div>
                <div className="bg-white p-4 rounded-xl text-center">
                  <TrendingUp className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                  <div className="text-xs text-gray-500">Projects</div>
                  <div className="font-semibold">
                    {service.stats?.projects}+
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl text-center">
                  <Award className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                  <div className="text-xs text-gray-500">Satisfaction</div>
                  <div className="font-semibold">
                    {service.stats?.satisfaction}%
                  </div>
                </div>
              </div>
              <Link to="/contact">
                <Button className="w-full mt-4 bg-gradient-to-r from-blue-600 to-cyan-500">
                  Get a Proposal
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {service.caseStudy && (
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-blue-100">
                <h3 className="text-xl font-bold mb-2">Case Study</h3>
                <div className="text-sm text-gray-500 mb-3">
                  {service.caseStudy.client}
                </div>
                <div className="font-semibold mb-3 text-gray-800">
                  {service.caseStudy.result}
                </div>
                <div className="flex flex-wrap gap-2">
                  {service.caseStudy.metrics.map((m, i) => (
                    <span
                      key={i}
                      className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium"
                    >
                      {m}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {galleryOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="w-full max-w-[1100px] h-[80vh] bg-transparent relative"
            >
              <button
                onClick={() => setGalleryOpen(false)}
                className="absolute top-4 right-4 z-20 text-white bg-black/30 p-2 rounded-full"
              >
                Close
              </button>
              <Swiper
                modules={[Pagination]}
                pagination={{ clickable: true }}
                initialSlide={galleryStartIndex}
                className="h-full rounded-lg overflow-hidden"
              >
                {combinedImages.map((src, i) => (
                  <SwiperSlide key={i}>
                    <div className="w-full h-full flex items-center justify-center bg-black">
                      <img
                        src={src}
                        alt={`image ${i + 1}`}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ServiceDetail;